import { CASH_REGISTER_KIND_LABELS, type CashRegister } from "@station/contracts";
import { Money } from "@station/core";
import {
  Button,
  DataTable,
  type DataTableColumn,
  Dialog,
  EmptyState,
  MoneyText,
  StatusBadge,
  useToast,
} from "@station/ui";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Banknote, Pencil, Plus } from "lucide-react";
import { type ReactElement, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { companySearchShape } from "../../../../features/accounting/company.js";
import { CashRegisterForm } from "../../../../features/treasury/cash-register-form.js";
import { useCashRegisters } from "../../../../features/treasury/definitions.js";
import { TreasuryPage } from "../../../../features/treasury/shared.js";
import { exportRowsToExcel } from "../../../../lib/excel.js";
import { formatDateTime } from "../../../../lib/format.js";
import { usePermissions } from "../../../../lib/session.js";

export const Route = createFileRoute("/_app/finans/kasalar/")({
  validateSearch: (search) => z.object(companySearchShape).parse(search),
  component: CashRegistersPage,
});

function CashRegistersPage() {
  const { t } = useTranslation();
  const permissions = usePermissions();
  const [creating, setCreating] = useState(false);
  return (
    <TreasuryPage
      title={t("menu.cashRegisters")}
      description={t("treasury.cashRegisters.description")}
      actions={
        permissions.has("treasury.definition.manage") ? (
          <Button onClick={() => setCreating(true)}>
            <Plus aria-hidden="true" />
            {t("treasury.cashRegisters.create")}
          </Button>
        ) : null
      }
    >
      {({ companyId }) => (
        <CashRegisterList companyId={companyId} creating={creating} onCreatingChange={setCreating} />
      )}
    </TreasuryPage>
  );
}

function CashRegisterList({
  companyId,
  creating,
  onCreatingChange,
}: {
  companyId: string;
  creating: boolean;
  onCreatingChange: (open: boolean) => void;
}): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const canManage = usePermissions().has("treasury.definition.manage");
  const registers = useCashRegisters(companyId, { includeInactive: true });
  const [editing, setEditing] = useState<CashRegister | null>(null);

  const columns = useMemo<DataTableColumn<CashRegister>[]>(
    () => [
      {
        id: "code",
        header: t("treasury.fields.code"),
        hideable: false,
        cell: (row) => (
          <Link
            to="/finans/kasalar/$id"
            params={{ id: row.id }}
            className="font-mono font-medium text-primary underline-offset-4 hover:underline"
            onClick={(event) => event.stopPropagation()}
          >
            {row.code}
          </Link>
        ),
        exportValue: (row) => row.code,
      },
      {
        id: "name",
        header: t("treasury.fields.name"),
        hideable: false,
        cell: (row) => (
          <span className="flex flex-wrap items-center gap-2">
            <span className="font-medium">{row.name}</span>
            {row.isActive ? null : <StatusBadge tone="neutral">{t("common.inactive")}</StatusBadge>}
          </span>
        ),
        exportValue: (row) => row.name,
      },
      {
        id: "kind",
        header: t("treasury.fields.kind"),
        cell: (row) => CASH_REGISTER_KIND_LABELS[row.kind],
        exportValue: (row) => CASH_REGISTER_KIND_LABELS[row.kind],
      },
      {
        id: "station",
        header: t("treasury.fields.station"),
        cell: (row) => row.stationName ?? t("treasury.fields.headOffice"),
        exportValue: (row) => row.stationName ?? t("treasury.fields.headOffice"),
      },
      {
        id: "ledgerAccount",
        header: t("treasury.fields.ledgerAccount"),
        cell: (row) => <span className="tabular">{row.ledgerAccountCode ?? "—"}</span>,
        exportValue: (row) => row.ledgerAccountCode,
      },
      {
        id: "balance",
        header: t("treasury.fields.balance"),
        align: "right",
        cell: (row) => <MoneyText value={row.balance} colorize className="font-medium" />,
        exportValue: (row) => Money.fromMinor(row.balance).formatTr({ symbol: false }),
      },
      {
        id: "lastCountAt",
        header: t("treasury.fields.lastCount"),
        cell: (row) => <span className="tabular">{formatDateTime(row.lastCountAt)}</span>,
        exportValue: (row) => formatDateTime(row.lastCountAt),
      },
      ...(canManage
        ? [
            {
              id: "actions",
              header: t("common.actions"),
              align: "right" as const,
              cell: (row: CashRegister) => (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(event) => {
                    event.stopPropagation();
                    setEditing(row);
                  }}
                  aria-label={t("treasury.cashRegisters.editOne", { name: row.name })}
                >
                  <Pencil aria-hidden="true" />
                  {t("common.edit")}
                </Button>
              ),
            },
          ]
        : []),
    ],
    [t, canManage],
  );

  return (
    <>
      <DataTable
        caption={t("menu.cashRegisters")}
        columns={columns}
        rows={registers.data ?? []}
        rowKey={(row) => row.id}
        loading={registers.isPending}
        emptyState={
          <EmptyState
            headingLevel={2}
            icon={<Banknote />}
            title={t("treasury.cashRegisters.emptyTitle")}
            description={t("treasury.cashRegisters.emptyDescription")}
            action={
              canManage ? (
                <Button onClick={() => onCreatingChange(true)}>
                  <Plus aria-hidden="true" />
                  {t("treasury.cashRegisters.create")}
                </Button>
              ) : undefined
            }
          />
        }
        onExport={async (visible) => {
          await exportRowsToExcel({
            fileName: "kasalar",
            sheetName: t("menu.cashRegisters"),
            columns: visible,
            rows: registers.data ?? [],
          });
        }}
        onRowClick={(row) => void navigate({ to: "/finans/kasalar/$id", params: { id: row.id } })}
      />
      <Dialog
        open={creating || editing !== null}
        onOpenChange={(open) => {
          if (open) return;
          onCreatingChange(false);
          setEditing(null);
        }}
        title={editing ? t("treasury.cashRegisters.edit") : t("treasury.cashRegisters.create")}
        description={t("treasury.cashRegisters.dialogDescription")}
      >
        <CashRegisterForm
          key={editing?.id ?? "new"}
          companyId={companyId}
          {...(editing ? { register: editing } : {})}
          onSaved={(saved) => {
            toast({ tone: "success", title: t("treasury.cashRegisters.saved", { name: saved.name }) });
            onCreatingChange(false);
            setEditing(null);
          }}
          onCancel={() => {
            onCreatingChange(false);
            setEditing(null);
          }}
        />
      </Dialog>
    </>
  );
}
